import posthog from "posthog-js";
import React from "react";
import { toast } from "sonner";

import Spinner from "../../../components/Spinner";
import { resendVerificationEmail } from "../../../services/auth/cognito";

const COOLDOWN_SECONDS = 60;

interface ResendVerificationButtonProps {
	email: string;
	/** Where the button was rendered from — used to segment the posthog event. */
	source: "verify_screen" | "signin";
	className?: string;
}

function ResendVerificationButton({ email, source, className }: ResendVerificationButtonProps) {
	const [loading, setLoading] = React.useState(false);
	const [secondsLeft, setSecondsLeft] = React.useState(0);

	React.useEffect(() => {
		if (secondsLeft <= 0) return;

		const timer = setTimeout(() => setSecondsLeft((seconds) => seconds - 1), 1000);
		return () => clearTimeout(timer);
	}, [secondsLeft]);

	async function onResend() {
		if (loading || secondsLeft > 0) return;

		setLoading(true);
		try {
			await resendVerificationEmail(email);
			posthog.capture("verification_email_resent", { source });
			toast.success(`We've sent a new activation link to ${email}`);
			setSecondsLeft(COOLDOWN_SECONDS);
		} catch (error) {
			toast.error((error as Error).message);
		} finally {
			setLoading(false);
		}
	}

	const disabled = loading || secondsLeft > 0;

	return (
		<div className={className}>
			<p className="text-sm text-[#5C5C5C]">
				Didn&#39;t get the email? Check your spam folder, or{" "}
				{/* `button { @apply btn flex items-center }` in index.css would make this a block-level flex
				    box on its own line — inline/p-0 puts it back in the flow of the sentence. */}
				<button
					type="button"
					onClick={onResend}
					disabled={disabled}
					className="inline p-0 leading-[inherit] font-semibold text-primary-700 underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
				>
					{loading ? (
						<span className="inline-flex items-center gap-2">
							<Spinner size={14} /> Sending...
						</span>
					) : secondsLeft > 0 ? (
						`resend in ${secondsLeft}s`
					) : (
						"resend the activation link"
					)}
				</button>
			</p>
		</div>
	);
}

export default ResendVerificationButton;
